"use client";

import { useEffect, useState, type CSSProperties } from "react";

import { useMediaQuery } from "#/hooks/useMediaQuery";
import { cn } from "#/lib/utils";

import {
  PAGE_BACKGROUND_BLUR_PX,
  PAGE_BACKGROUND_MIN_WIDTH_PX,
  PAGE_BACKGROUND_PREVIEW_SCALE,
  PAGE_BACKGROUND_SCROLL_SPEED_PX_PER_SEC,
} from "./pageBackground/constants";
import { todayPremiumPreviewUrl, todayStandardPreviewUrl } from "./pageBackground/map";

type PageBackgroundTile = {
  url: string;
  width: number;
  height: number;
};

type PageBackgroundState = {
  left: PageBackgroundTile;
  right: PageBackgroundTile;
  durationSec: number;
};

type PageBackgroundProps = {
  className?: string;
};

function syncedScrollDurationSec(left: PageBackgroundTile, right: PageBackgroundTile): number {
  return Math.max(left.width, right.width) / PAGE_BACKGROUND_SCROLL_SPEED_PX_PER_SEC;
}

function loadPageBackgroundTile(previewUrl: string): Promise<PageBackgroundTile | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        resolve(null);
        return;
      }

      resolve({
        url: previewUrl,
        width: img.naturalWidth * PAGE_BACKGROUND_PREVIEW_SCALE,
        height: img.naturalHeight * PAGE_BACKGROUND_PREVIEW_SCALE,
      });
    };
    img.onerror = () => resolve(null);
    img.src = previewUrl;
  });
}

function usePageBackgroundTilesEnabled() {
  return useMediaQuery(`(min-width: ${PAGE_BACKGROUND_MIN_WIDTH_PX}px)`);
}

function PageBackgroundHalf({
  tile,
  side,
  durationSec,
}: {
  tile: PageBackgroundTile;
  side: "left" | "right";
  durationSec: number;
}) {
  return (
    <div
      className={cn(
        "page-background-scroll absolute inset-y-0 w-1/2 [image-rendering:pixelated]",
        side === "left" ? "left-0" : "right-0",
      )}
      style={
        {
          backgroundImage: `url(${tile.url})`,
          backgroundSize: `${tile.width}px ${tile.height}px`,
          animationDuration: `${durationSec}s`,
          filter: `blur(${PAGE_BACKGROUND_BLUR_PX}px)`,
          "--page-background-tile-w": `${tile.width}px`,
          "--page-background-tile-h": `${tile.height}px`,
        } as CSSProperties
      }
    />
  );
}

export function PageBackground({ className }: PageBackgroundProps) {
  const tilesEnabled = usePageBackgroundTilesEnabled();
  const [background, setBackground] = useState<PageBackgroundState | null>(null);

  useEffect(() => {
    if (!tilesEnabled) return;

    let cancelled = false;

    void Promise.all([
      loadPageBackgroundTile(todayStandardPreviewUrl()),
      loadPageBackgroundTile(todayPremiumPreviewUrl()),
    ]).then(([left, right]) => {
      if (cancelled || !left || !right) return;
      setBackground({
        left,
        right,
        durationSec: syncedScrollDurationSec(left, right),
      });
    });

    return () => {
      cancelled = true;
    };
  }, [tilesEnabled]);

  return (
    <div
      aria-hidden
      className={cn("pointer-events-none fixed inset-0 -z-10 bg-background", className)}
    >
      {tilesEnabled && background ? (
        <>
          <PageBackgroundHalf
            tile={background.left}
            side="left"
            durationSec={background.durationSec}
          />
          <PageBackgroundHalf
            tile={background.right}
            side="right"
            durationSec={background.durationSec}
          />
          <div className="page-background-seam absolute inset-y-0" />
        </>
      ) : null}
      <div className="absolute inset-0 bg-background/10" />
    </div>
  );
}
